import { useMemo, useCallback } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DateRangeIcon from '@mui/icons-material/DateRange';

/**
 * Barra de filtros locales para el Dashboard de Acumuladores de Agentes.
 * Todos los filtros operan sobre los datos ya cargados, sin peticiones HTTP.
 *
 * Props:
 *  - rawRows: array original completo de Table1
 *  - filters: { fechas, horaDesde, horaHasta, minAgentes, soloConActividad }
 *  - onChange: (newFilters) => void
 */

const HORAS = Array.from({ length: 25 }, (_, i) => {
  const h = String(i).padStart(2, '0');
  return { value: i, label: `${h}:00` };
});

const FilterBarAcuAgentes = ({ rawRows, filters, onChange }) => {
  const theme = useTheme();
  const dk = theme.palette.mode === 'dark';

  // Extraer fechas únicas de los datos
  const fechasDisponibles = useMemo(() => {
    if (!rawRows || rawRows.length === 0) return [];
    const set = new Set();
    rawRows.forEach((r) => {
      const d = new Date(r.Intervalo);
      if (!isNaN(d)) set.add(d.toISOString().split('T')[0]);
    });
    return [...set].sort();
  }, [rawRows]);

  // Formato legible para los selects de fecha
  const fmtFechaCorta = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    const dias = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    const dt = new Date(iso + 'T12:00:00');
    const dia = dias[dt.getDay()];
    return `${dia} ${d}/${m}/${y}`;
  };

  // Rango de fechas seleccionado
  const fechaDesdeVal = filters.fechaDesde || fechasDisponibles[0] || '';
  const fechaHastaVal = filters.fechaHasta || fechasDisponibles[fechasDisponibles.length - 1] || '';

  const fechasEnRango = useMemo(() => {
    if (!fechaDesdeVal || !fechaHastaVal || fechasDisponibles.length === 0) return fechasDisponibles;
    return fechasDisponibles.filter((f) => f >= fechaDesdeVal && f <= fechaHastaVal);
  }, [fechaDesdeVal, fechaHastaVal, fechasDisponibles]);

  const handleFechaDesde = useCallback((e) => {
    const val = e.target.value;
    const hasta = filters.fechaHasta || fechasDisponibles[fechasDisponibles.length - 1] || val;
    const nuevasFechas = fechasDisponibles.filter((f) => f >= val && f <= hasta);
    onChange({ ...filters, fechaDesde: val, fechas: nuevasFechas });
  }, [filters, fechasDisponibles, onChange]);

  const handleFechaHasta = useCallback((e) => {
    const val = e.target.value;
    const desde = filters.fechaDesde || fechasDisponibles[0] || val;
    const nuevasFechas = fechasDisponibles.filter((f) => f >= desde && f <= val);
    onChange({ ...filters, fechaHasta: val, fechas: nuevasFechas });
  }, [filters, fechasDisponibles, onChange]);

  const resetFilters = () => {
    onChange({
      fechas: [...fechasDisponibles],
      fechaDesde: fechasDisponibles[0] || '',
      fechaHasta: fechasDisponibles[fechasDisponibles.length - 1] || '',
      horaDesde: 0,
      horaHasta: 24,
      minAgentes: 0,
      soloConActividad: false,
    });
  };

  const diasEnRango = fechasEnRango.length;

  // Conteo de filas filtradas
  const filteredCount = useMemo(() => {
    if (!rawRows) return 0;
    return aplicarFiltros(rawRows, filters).length;
  }, [rawRows, filters]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 2,
        borderColor: dk ? '#334155' : '#cbd5e1',
        bgcolor: dk ? 'rgba(30,41,59,0.6)' : 'rgba(248,250,252,0.8)',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <FilterListIcon sx={{ fontSize: 18, color: dk ? '#94a3b8' : '#64748b' }} />
        <Typography variant="subtitle2" sx={{ color: dk ? '#e2e8f0' : '#334155', fontWeight: 600 }}>
          Filtros
        </Typography>
        <Typography variant="caption" sx={{ color: dk ? '#64748b' : '#94a3b8' }}>
          ({filteredCount} de {rawRows?.length || 0} intervalos)
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Restablecer filtros">
          <IconButton size="small" onClick={resetFilters}>
            <RestartAltIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Fila 1: Rango de Fechas */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <DateRangeIcon sx={{ fontSize: 18, color: dk ? '#94a3b8' : '#64748b' }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ fontSize: 12 }}>Fecha desde</InputLabel>
          <Select
            value={fechaDesdeVal}
            label="Fecha desde"
            onChange={handleFechaDesde}
            sx={{ fontSize: 12, height: 32 }}
          >
            {fechasDisponibles
              .filter((f) => f <= fechaHastaVal)
              .map((f) => (
                <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>
                  {fmtFechaCorta(f)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ fontSize: 12 }}>Fecha hasta</InputLabel>
          <Select
            value={fechaHastaVal}
            label="Fecha hasta"
            onChange={handleFechaHasta}
            sx={{ fontSize: 12, height: 32 }}
          >
            {fechasDisponibles
              .filter((f) => f >= fechaDesdeVal)
              .map((f) => (
                <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>
                  {fmtFechaCorta(f)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Chip
          label={`${diasEnRango} día${diasEnRango !== 1 ? 's' : ''}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: 11, height: 22 }}
        />
      </Box>

      {/* Fila 2: Hora, Min Agentes, Solo con actividad */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel sx={{ fontSize: 12 }}>Hora desde</InputLabel>
          <Select
            value={filters.horaDesde ?? 0}
            label="Hora desde"
            onChange={(e) => onChange({ ...filters, horaDesde: e.target.value })}
            sx={{ fontSize: 12, height: 32 }}
          >
            {HORAS.filter((h) => h.value < (filters.horaHasta ?? 24)).map((h) => (
              <MenuItem key={h.value} value={h.value} sx={{ fontSize: 12 }}>
                {h.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel sx={{ fontSize: 12 }}>Hora hasta</InputLabel>
          <Select
            value={filters.horaHasta ?? 24}
            label="Hora hasta"
            onChange={(e) => onChange({ ...filters, horaHasta: e.target.value })}
            sx={{ fontSize: 12, height: 32 }}
          >
            {HORAS.filter((h) => h.value > (filters.horaDesde ?? 0)).map((h) => (
              <MenuItem key={h.value} value={h.value} sx={{ fontSize: 12 }}>
                {h.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Min. agentes"
          type="number"
          size="small"
          value={filters.minAgentes ?? 0}
          onChange={(e) => onChange({ ...filters, minAgentes: Math.max(0, parseInt(e.target.value) || 0) })}
          inputProps={{ min: 0, style: { fontSize: 12, width: 40 } }}
          InputLabelProps={{ sx: { fontSize: 12 } }}
          sx={{ width: 110, '& .MuiInputBase-root': { height: 32 } }}
        />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={!!filters.soloConActividad}
              onChange={(e) => onChange({ ...filters, soloConActividad: e.target.checked })}
            />
          }
          label={
            <Typography variant="caption" sx={{ fontSize: 12 }}>
              Solo con actividad
            </Typography>
          }
          sx={{ ml: 0 }}
        />
      </Box>
    </Paper>
  );
};

/**
 * Aplica los filtros locales sobre las filas crudas.
 * Exportada para ser usada en DashboardAcuAgentes.
 */
export function aplicarFiltros(rows, filters) {
  if (!rows || rows.length === 0) return [];
  if (!filters) return rows;

  let filtered = rows;

  // Filtro por fechas seleccionadas
  if (filters.fechas && filters.fechas.length > 0) {
    filtered = filtered.filter((r) => {
      const d = new Date(r.Intervalo);
      const iso = d.toISOString().split('T')[0];
      return filters.fechas.includes(iso);
    });
  }

  // Filtro por rango horario
  const hDesde = filters.horaDesde ?? 0;
  const hHasta = filters.horaHasta ?? 24;
  if (hDesde > 0 || hHasta < 24) {
    filtered = filtered.filter((r) => {
      const d = new Date(r.Intervalo);
      const h = d.getHours() + d.getMinutes() / 60;
      return h >= hDesde && h < hHasta;
    });
  }

  // Filtro por mínimo de agentes
  const minAg = filters.minAgentes ?? 0;
  if (minAg > 0) {
    filtered = filtered.filter((r) => {
      return (parseFloat(r.CantAgentesUnicos) || 0) >= minAg;
    });
  }

  // Solo intervalos con actividad (al menos 1 llamada o login > 0)
  if (filters.soloConActividad) {
    filtered = filtered.filter((r) => {
      const n = (v) => parseFloat(v) || 0;
      const llamadas =
        n(r.EntrantesAtendidas) + n(r.EntrantesAbandonadas) +
        n(r.SalientesAtendidas) + n(r.SalientesNoAtendidas) +
        n(r.DiscadorAtendidas);
      return llamadas > 0 || n(r.Login) > 0;
    });
  }

  return filtered;
}

export default FilterBarAcuAgentes;
